import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import { ChatHistoryListProps } from '@/types/components/converse/history';
import { ChatHistoryEntryType } from '@/types/converse/chatHistory';
import groupChatHistory from '@/utils/converse/groupChatHistory';
import {
  TextField,
  CircularProgress,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ChatHistoryItem from './ChatHistoryItem';
import { useInView } from 'react-intersection-observer';

type GroupedHistory = ReturnType<typeof groupChatHistory>;
type GroupKey = keyof GroupedHistory;

interface SortedGroup {
  key: string;
  title: string;
  chats: ChatHistoryEntryType[];
}

function ChatHistoryList({
  currentChatID,
  setCurrentChatID,
  fetchNextPage,
  hasMore,
  refetchHistory,
  page,
}: ChatHistoryListProps): React.ReactNode {
  const { chatHistory } = useAlchemystStoreForAi((store) => ({
    chatHistory: store.chatHistory,
  }));

  const [filter, setFilter] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '100px',
  });

  // Handle infinite scroll properly with actual fetch status
  useEffect(() => {
    if (inView && hasMore && !isLoading && fetchNextPage) {
      setIsLoading(true);
      const fetchData = async () => {
        try {
          await fetchNextPage();
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [inView, hasMore, isLoading, fetchNextPage]);

  // Step 1: Memoize the initial chat processing
  const processedChatHistory = useMemo(() => {
    // First sort by pinned status (pinned first), then by timestamp (newest first)
    const sortedChats = [...chatHistory].sort((a, b) => {
      // First sort by pinned status (pinned first)
      if ((a.pinned && b.pinned) || (!a.pinned && !b.pinned)) {
        // If both are pinned or both are not pinned, sort by timestamp
        return b.timestamp - a.timestamp;
      }
      // Pinned chats come first
      return a.pinned ? -1 : 1;
    });
    
    const chatMap = new Map<string, ChatHistoryEntryType>();

    // Process all chats in one pass, keeping only the most recent version
    sortedChats.forEach((chat) => {
      const existingChat = chatMap.get(chat.title);
      if (!existingChat) {
        chatMap.set(chat.title, chat);
      }
    });

    return Array.from(chatMap.values());
  }, [chatHistory]);

  // Step 2: Memoize the filtering based on search
  const filteredChatHistory = useMemo(() => {
    if (!filter) {
      return processedChatHistory;
    }
    const lowerFilter = filter.toLowerCase();
    return processedChatHistory.filter((chat) =>
      chat.title.toLowerCase().includes(lowerFilter)
    );
  }, [processedChatHistory, filter]);

  // Step 3: Create a separate group for pinned chats and then group the rest
  const groupedAndSortedHistory = useMemo(() => {
    // Separate pinned and unpinned chats
    const pinnedChats = filteredChatHistory.filter(chat => chat.pinned);
    const unpinnedChats = filteredChatHistory.filter(chat => !chat.pinned);
    
    // Group unpinned chats as before
    const groups = groupChatHistory(unpinnedChats);
    
    // Create the result array with pinned group first if there are any pinned chats
    const result: SortedGroup[] = [];
    
    if (pinnedChats.length > 0) {
      result.push({
        key: 'pinned',
        title: 'Pinned',
        chats: pinnedChats,
      });
    }
    
    // Add the regular grouped chats
    result.push(
      ...(Object.keys(groups) as GroupKey[]).map((groupKey) => ({
        key: groupKey,
        title: groupKey[0].toUpperCase() + groupKey.slice(1).replace(/_/g, ' '),
        chats: groups[groupKey],
      }))
    );
    
    return result;
  }, [filteredChatHistory]);

  // Step 4: Memoize UI handlers
  const handleFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFilter(e.target.value);
    },
    []
  );

  return (
    <Stack spacing={2} mt={1}>
      <TextField
        value={filter}
        onChange={handleFilterChange}
        label="Search by chat title"
      />
      {filteredChatHistory.length === 0 && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="150px"
        >
          <CircularProgress size={28} />
        </Box>
      )}
      {filteredChatHistory.length > 0 && (
        <Box
          sx={{
            overflowX: 'hidden',
            overflowY: 'auto',
          }}
        >
          {groupedAndSortedHistory.map(({ key, title, chats }: SortedGroup) => (
            <Stack key={key} spacing={1}>
              {chats.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 'bold',
                      padding: '6px',
                      paddingTop: '16px',
                      color: key === 'pinned' ? '#ff9a35' : '#757575',
                    }}
                  >
                    {title}
                  </Typography>
                  {chats.map((chat: ChatHistoryEntryType) => (
                    <ChatHistoryItem
                      key={chat.id}
                      chat={chat}
                      currentChatID={currentChatID}
                      setCurrentChatID={setCurrentChatID}
                      refetchHistory={refetchHistory}
                      page={page}
                    />
                  ))}
                </>
              )}
            </Stack>
          ))}
          {hasMore && <div ref={loadMoreRef} style={{ height: '20px' }} />}
          {isLoading && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={20} />
            </Box>
          )}
        </Box>
      )}
    </Stack>
  );
}

export default ChatHistoryList;
